import { useState, useEffect } from 'react';
import { useAppContext } from '@/contexts/AppContext.js';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Input } from '@/components/ui/input.js';
import { Button } from '@/components/ui/button.js';
import { Save, FileText, Eye, Edit3 } from 'lucide-react';
import { MarkdownText } from '@/components/ui/markdown-text.js';

interface NoteArtifactProps {
  tabId: string;
}

export function NoteArtifact({ tabId }: NoteArtifactProps) {
  const { notes, updateCurrentNote, openNote, currentNote } = useAppContext();
  const { artifacts, updateArtifact } = useArtifactPanelStore();

  const artifact = artifacts[tabId];
  const noteId = (artifact?.data as { noteId?: string })?.noteId;

  // Find the note from the notes list
  const note = notes.find((n) => n.id === noteId || `note-${n.id}` === tabId);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Get viewMode from artifact metadata, default to 'preview'
  const isPreviewMode = artifact?.metadata?.viewMode !== 'edit';

  // Load note data when note changes
  useEffect(() => {
    if (note) {
      setTitle(note.title);
      setContent(note.content);
      setHasChanges(false);
      // Also set as current note in context for the update function
      if (currentNote?.id !== note.id) {
        openNote(note);
      }
    }
  }, [note?.id, note?.updatedAt]);

  if (!note) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Note not found</p>
      </div>
    );
  }

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    setHasChanges(true);
    // Update artifact title in real-time
    updateArtifact(tabId, { title: newTitle || 'Untitled Note' });
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (note && currentNote?.id === note.id) {
      updateCurrentNote({ title, content });
      setHasChanges(false);
    }
  };

  const toggleViewMode = () => {
    const newMode = isPreviewMode ? 'edit' : 'preview';
    updateArtifact(tabId, {
      metadata: {
        ...artifact?.metadata,
        viewMode: newMode,
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0">
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <Input
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Untitled Note"
            className="text-base font-medium border-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-auto"
          />
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-xs text-muted-foreground">Unsaved</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleViewMode}
            className="gap-2 h-8"
          >
            {isPreviewMode ? (
              <>
                <Edit3 className="h-3.5 w-3.5" />
                Edit
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                Preview
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges}
            className="gap-2 h-8"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {isPreviewMode ? (
          /* Preview Mode - Markdown Rendering */
          <div className="flex-1 px-8 py-6 overflow-y-auto">
            {content ? (
              <MarkdownText>{content}</MarkdownText>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                No content to preview. Switch to edit mode to start writing.
              </div>
            )}
          </div>
        ) : (
          /* Edit Mode - Textarea */
          <div className="flex-1 px-8 py-6 overflow-y-auto">
            <Textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Start writing your thoughts...

You can use Markdown:
- **bold** and *italic*
- Lists and tables
- Links and images
- And more!"
              className="h-full w-full text-sm leading-relaxed resize-none border-none focus-visible:ring-0 focus-visible:ring-offset-0 p-0 min-h-[500px]"
            />
          </div>
        )}
      </div>
    </div>
  );
}
